import type { TrustPilotBusinessType } from "@/api/reviewTrustPilotApi";
import type { CouponType } from "@/types/trustpilotPricing";

export function parseFormattedPrice(price: string | number): number {
  if (typeof price === "number") return Number.isFinite(price) ? price : 0;

  const normalized = price.trim().replace(/[^\d,.-]/g, "");
  if (!normalized) return 0;

  const lastComma = normalized.lastIndexOf(",");
  const lastPeriod = normalized.lastIndexOf(".");
  const lastSeparator = Math.max(lastComma, lastPeriod);
  const trailingDigits = normalized.length - lastSeparator - 1;
  const hasBothSeparators = lastComma >= 0 && lastPeriod >= 0;
  const decimalSeparator = hasBothSeparators
    ? lastComma > lastPeriod
      ? ","
      : "."
    : trailingDigits > 0 && trailingDigits <= 2
      ? lastComma > lastPeriod
        ? ","
        : "."
      : "";
  if (!decimalSeparator) {
    const parsedInteger = Number.parseFloat(normalized.replace(/[,.]/g, ""));
    return Number.isFinite(parsedInteger) ? parsedInteger : 0;
  }
  const thousandsSeparator = decimalSeparator === "," ? "." : ",";
  const parseable = normalized
    .replaceAll(thousandsSeparator, "")
    .replace(decimalSeparator, ".");
  const parsed = Number.parseFloat(parseable);

  return Number.isFinite(parsed) ? parsed : 0;
}

export default function getUserSelectedReviewsCount(
  selectedCompanies: TrustPilotBusinessType[],
): number {
  return selectedCompanies.reduce(
    (total, company) => total + (company.userSelectedReviews?.length ?? 0),
    0,
  );
}

export function filterSelectedCompaniesWithReviewsTrustPilot(
  companies: TrustPilotBusinessType[],
) {
  return companies.filter((company) => (company.userSelectedReviews?.length ?? 0) > 0);
}

type CalculateOrderPriceOptions = {
  totalPriceBefore?: number;
  freeRemovalPricePerItem?: number;
};

export function calculateOrderPrice(
  couponData: CouponType | null | undefined,
  pricePerItem: number,
  reviewCount: number,
  options: CalculateOrderPriceOptions = {},
) {
  const totalPriceBefore = options.totalPriceBefore ?? pricePerItem * reviewCount;

  if (couponData?.discount) {
    return applyPercentageDiscount(totalPriceBefore, couponData.discount);
  }

  if (couponData?.flat_price) {
    return roundMoney(couponData.flat_price * reviewCount);
  }

  if (couponData?.free_request_removals) {
    const freeRemovalCount = Math.min(
      Math.max(0, couponData.free_request_removals),
      Math.max(0, reviewCount),
    );
    const freeRemovalPrice = options.freeRemovalPricePerItem ?? pricePerItem;
    return roundMoney(Math.max(0, totalPriceBefore - freeRemovalCount * freeRemovalPrice));
  }

  return roundMoney(totalPriceBefore);
}

export function applyPercentageDiscount(totalPrice: number, discount: number) {
  return roundMoney(totalPrice * (1 - discount / 100));
}

function roundMoney(amount: number) {
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}
